import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { changeLanguage } from '../i18n/config';
import { API_BASE_URL } from '../config/apiUrl';

import { createBillingPortalSession, billingPortalErrorKey } from '../lib/billingPortal';
import { DeleteAccountSection } from '../components/features/DeleteAccountSection';
import { useSeniors } from '../hooks/useSeniors';
interface CaregiverProfile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  language?: string;
}

export const MyAccount: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { seniors } = useSeniors();
  const { user } = useAuth();

  useEffect(() => {
    document.title = i18n.language.startsWith('fr')
      ? 'Mon compte — AidFone'
      : 'My account — AidFone';
  }, [i18n.language]);
  const [profile, setProfile] = useState<CaregiverProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState<string | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const language = i18n.language?.startsWith('fr') ? 'fr' : 'en';

  const handleLanguageChange = (lang: 'en' | 'fr') => {
    changeLanguage(lang);
    if (profile) {
      supabase.from('caregivers').update({ language: lang }).eq('id', profile.id).then();
    }
  };

  const apiUrl = API_BASE_URL;

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const fetchProfile = async () => {
      try {
        const { data, error } = await supabase
          .from('caregivers')
          .select('*')
          .eq('id', user.id)
          .single();

        if (error) throw error;
        if (data) {
          setProfile(data);
          // Caregivers table uses full_name; split for first/last UI fields
          const nameParts = (data.full_name || '').split(' ');
          setFirstName(data.first_name || nameParts[0] || '');
          setLastName(data.last_name || nameParts.slice(1).join(' ') || '');
          setEmail(data.email || user.email || '');
          setPhone(data.phone || '');
          // Don't auto-flip the UI language from the saved DB value — the
          // `caregivers.language` column DEFAULTS to 'fr' (Quebec launch
          // market), so visiting /account when the user has never explicitly
          // set a preference forced the whole app into French. The language
          // toggle below persists the user's actual choice; that's the only
          // path that should change i18n state.
        }
      } catch (err) {
        console.error('Failed to fetch profile:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [user]);

  const handleSave = async () => {
    if (!profile) return;
    setSaving(true);
    setSaved(false);
    try {
      const { error } = await supabase
        .from('caregivers')
        .update({
          first_name: firstName,
          last_name: lastName,
          email,
          phone,
        })
        .eq('id', profile.id);

      if (error) throw error;
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Failed to save profile:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleManageBilling = async () => {
    // Every failure path here used to be silent — see lib/billingPortal.ts.
    setBillingError(null);
    setBillingLoading(true);
    try {
      const result = await createBillingPortalSession();
      if (result.ok) {
        window.location.href = result.url;
        return;
      }
      console.error('[MyAccount] Billing portal failed:', result);
      setBillingError(t(billingPortalErrorKey(result)));
    } finally {
      setBillingLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-3 border-brand-primary/20 border-t-brand-primary rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">{t('account.title')}</h1>
          <p className="text-gray-500 mt-1">{t('account.subtitle')}</p>
        </div>

        {/* Profile Section */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span className="material-symbols-outlined text-brand-primary" style={{ fontSize: '20px' }}>person</span>
            {t('account.profileInfo')}
          </h2>

          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('account.firstName')}</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={e => setFirstName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('account.lastName')}</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={e => setLastName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('account.email')}</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('account.phone')}</label>
              <input
                type="tel"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
                placeholder="(514) 555-1234"
              />
            </div>
          </div>
        </div>

        {/* Language Section */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span className="material-symbols-outlined text-brand-primary" style={{ fontSize: '20px' }}>language</span>
            {t('account.language')}
          </h2>
          <div className="flex gap-3">
            <button
              onClick={() => handleLanguageChange('en')}
              className={`px-4 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                language === 'en'
                  ? 'bg-brand-primary text-white border-brand-primary'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-brand-primary/30'
              }`}
            >
              English
            </button>
            <button
              onClick={() => handleLanguageChange('fr')}
              className={`px-4 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                language === 'fr'
                  ? 'bg-brand-primary text-white border-brand-primary'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-brand-primary/30'
              }`}
            >
              Français
            </button>
          </div>
        </div>

        {/* Billing Section */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span className="material-symbols-outlined text-brand-primary" style={{ fontSize: '20px' }}>credit_card</span>
            {t('account.billing')}
          </h2>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <p className="text-sm text-gray-900 font-semibold">{t('account.planSummary')}</p>
              <p className="text-xs text-gray-500 mt-0.5">{t('account.cancelAnytime')}</p>
            </div>
            <button
              onClick={handleManageBilling}
              disabled={billingLoading}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-brand-primary rounded-lg hover:bg-brand-primary/90 transition-colors shadow-sm whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span
                className={`material-symbols-outlined ${billingLoading ? 'animate-spin' : ''}`}
                style={{ fontSize: '18px' }}
              >
                {billingLoading ? 'progress_activity' : 'open_in_new'}
              </span>
              {billingLoading ? t('billingPortal.opening') : t('account.manageBilling')}
            </button>
          </div>

          {/* The whole point of this fix: a failure is now visible. The button
              used to return silently on the most common case (no Stripe
              customer yet), which reads as a broken product. */}
          {billingError && (
            <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5">
              <p className="text-sm text-amber-900">{billingError}</p>
            </div>
          )}
        </div>

        {/* Danger zone — last on the page, deliberately. A destructive action
            does not belong beside everyday controls. */}
        <DeleteAccountSection seniorName={seniors[0]?.name ?? null} />

        {/* Save Button */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2.5 bg-brand-primary text-white text-sm font-semibold rounded-lg hover:bg-brand-primary/90 transition-colors disabled:opacity-50"
          >
            {saving ? `${t('account.saveChanges')}…` : t('account.saveChanges')}
          </button>
          {saved && (
            <span className="text-sm text-emerald-600 font-medium flex items-center gap-1">
              <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>check_circle</span>
              {t('account.saved')}
            </span>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default MyAccount;
